import styled, { css, keyframes } from "styled-components";

const breathe = keyframes`
  0%, 100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.015);
  }
`;

const generatePulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow:
      0 0 0 0 rgba(63, 136, 247, 0.4),
      inset 0 0 10px rgba(63, 136, 247, 0.25);
  }
  50% {
    transform: scale(0.97);
    box-shadow:
      0 0 22px 6px rgba(63, 136, 247, 0.55),
      inset 0 0 18px rgba(63, 136, 247, 0.35);
  }
`;

const generateExplode = keyframes`
  0% {
    transform: scale(0.94);
    box-shadow:
      0 0 22px 6px rgba(63, 136, 247, 0.55),
      inset 0 0 20px rgba(63, 136, 247, 0.35);
  }
  30% {
    transform: scale(1.12);
    box-shadow:
      0 0 40px 14px rgba(63, 136, 247, 0.85),
      0 0 60px 24px rgba(45, 120, 255, 0.5),
      inset 0 0 28px rgba(255, 255, 255, 0.25);
  }
  50% {
    transform: scale(1.06);
  }
  70% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(63, 136, 247, 0);
  }
`;

const generateShimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const adCtaShimmer = keyframes`
  0% { background-position: -240% center, center; }
  100% { background-position: 240% center, center; }
`;

export const Wrapper = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${(p) => (p.$padding ? "16px" : "56px 16px 40px")};
  /* overflow: hidden; */
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

export const Glow = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1512px;
  height: 249px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.55;
  filter: blur(8px);
  background: radial-gradient(
    37.01% 37.12% at 50% -2.81%,
    #4383d3 0%,
    rgba(4, 4, 5, 0) 100%
  );
`;

export const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;

  > *:not([data-spotlight="true"]) {
    transition:
      opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1),
      filter 0.55s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  }

  ${(props) =>
    props.$promptFocused &&
    css`
      > *:not([data-spotlight="true"]) {
        opacity: 0.35;
        filter: blur(3px);
        transform: scale(0.985);
      }
    `}
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

export const HeroAdBanner = styled.a`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 176px;
  width: 100%;
  max-width: 640px;
  height: 136px;
  overflow: hidden;
  border-radius: 22px;
  isolation: isolate;
  text-decoration: none;
  color: #fff;
  background:
    radial-gradient(
      84% 115% at 38% 0%,
      rgba(37, 99, 176, 0.26) 0%,
      rgba(3, 13, 27, 0) 54%
    ),
    linear-gradient(135deg, #06152b 0%, #081d36 48%, #02060c 100%);
  box-shadow:
    0 24px 42px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(151, 197, 255, 0.1),
    inset 0 0 38px rgba(31, 105, 200, 0.16);
  transition:
    transform 0.18s ease,
    filter 0.18s ease,
    box-shadow 0.22s ease;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
    box-shadow:
      0 28px 50px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(151, 197, 255, 0.15),
      inset 0 0 44px rgba(31, 105, 200, 0.2);
  }

  &:hover video {
    transform: scale(1.035);
    filter: saturate(1.08) brightness(1.04);
  }

  &:hover [data-hero-ad-button="true"] {
    transform: translateY(-1px);
    filter: brightness(1.06);
  }

  &:hover [data-hero-ad-button="true"]::after {
    transform: translateX(2px) rotate(45deg);
  }

  &:active {
    transform: translateY(0) scale(0.988);
    filter: brightness(1.08);
  }

  &:active video {
    transform: scale(1.045);
    filter: saturate(1.1) brightness(1.05);
  }

  &:active [data-hero-ad-button="true"] {
    transform: scale(0.98);
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid rgba(151, 197, 255, 0.85);
    outline-offset: 4px;
  }

  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr) 146px;
    height: 122px;
    border-radius: 18px;
  }

  @media (max-width: 390px) {
    grid-template-columns: minmax(0, 1fr) 135px;
  }
`;

export const HeroAdContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  padding: 14px 18px 14px 24px;

  @media (max-width: 700px) {
    gap: 7px;
    padding: 10px 8px 10px 12px;
  }

  @media (max-width: 390px) {
    gap: 6px;
    padding: 10px 6px 10px 10px;
  }
`;

export const HeroAdBadge = styled.span`
  color: rgba(37, 95, 168, 0.42);
  font-family: Figtree;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0;

  @media (max-width: 700px) {
    font-size: 10px;
  }
`;

export const HeroAdTitle = styled.span`
  color: #fff;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: 0;

  @media (max-width: 700px) {
    font-size: 14px;
    line-height: 1.1;
  }

  @media (max-width: 390px) {
    font-size: 13px;
  }
`;

export const HeroAdSubtitle = styled.span`
  color: rgba(213, 231, 255, 0.75);
  font-family: Figtree;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.12;
  letter-spacing: 0;

  @media (max-width: 700px) {
    font-size: 12px;
  }

  @media (max-width: 390px) {
    font-size: 11px;
  }
`;

export const HeroAdButton = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 124px;
  height: 34px;
  padding: 0 18px;
  gap: 8px;
  border-radius: 999px;
  border: 2px solid rgba(120, 181, 255, 0.62);
  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 100%
    ),
    #3f91ff;
  color: #fff;
  font-family: Figtree;
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0;
  text-shadow: 0 1px 1px rgba(13, 64, 142, 0.2);
  box-shadow:
    0 5px 12px rgba(47, 130, 255, 0.22),
    inset 0 1px 2px rgba(255, 255, 255, 0.2),
    inset 0 -8px 12px -8px rgba(177, 217, 255, 0.32);
  transition:
    transform 0.16s ease,
    filter 0.16s ease,
    box-shadow 0.16s ease;

  &::after {
    content: "";
    width: 7px;
    height: 7px;
    flex: 0 0 auto;
    border-top: 2px solid currentColor;
    border-right: 2px solid currentColor;
    transform: rotate(45deg);
    transition: transform 0.16s ease;
  }

  @media (max-width: 700px) {
    min-width: 98px;
    height: 29px;
    padding: 0 12px;
    gap: 6px;
    font-size: 14px;

    &::after {
      width: 6px;
      height: 6px;
      border-width: 1.8px;
    }
  }

  @media (max-width: 390px) {
    min-width: 92px;
    height: 28px;
    font-size: 13px;
  }
`;

export const HeroAdMedia = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
  height: 100%;
  clip-path: polygon(20% 0, 100% 0, 100% 100%, 0 100%);
  overflow: hidden;
`;

export const HeroAdVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: 50% 42%;
  transition:
    transform 0.22s ease,
    filter 0.22s ease;

  @media (max-width: 700px) {
    object-position: 47% 42%;
  }
`;

export const Eyebrow = styled.p`
  margin: 0;
  color: #84bafe;
  font-family: Figtree;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.27px;
  text-align: left;
`;

export const Heading = styled.h1`
  margin: 0;
  color: #fff;
  font-family: Figtree;
  font-size: 32px;
  font-weight: 700;
  line-height: 41px;
  letter-spacing: -0.48px;
  text-align: left;
`;

export const TabsRow = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 640px;
  height: 38px;
  padding: 2px;
  justify-content: center;
  align-items: center;
  border-radius: 999px;
  /* A step lighter than the page gradient behind it (which sits right at
     #0a1a2f in this region) plus a subtle ring, so the switcher stays
     visible on the hero. Inset shadow instead of a border keeps the
     slider geometry untouched. */
  background: #152a47;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
`;

export const TabSlider = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  width: calc(50% - 2px);
  height: calc(100% - 4px);
  border-radius: 9999px;
  border: 0.5px solid #2877d9;
  background: #4383d3;
  transform: translateX(
    ${(props) => (props.$active === "video" ? "100%" : "0")}
  );
  transition: transform 0.3s ease;
  z-index: 0;
`;

export const Tab = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1 0 0;
  padding: 6px 8px 6px 4px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 9999px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(props) => (props.$active ? "#FFF" : "rgba(255, 255, 255, 0.6)")};
  font-family: Figtree;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.21px;
  transition: color 0.3s ease;

  svg {
    opacity: ${(props) => (props.$active ? 1 : 0.6)};
    transition: opacity 0.3s ease;
  }
`;

export const PromptBoxWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  border-radius: 12px;
  cursor: text;
  transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  transform: scale(${(props) => (props.$focused ? 1.03 : 1)})
    translateY(${(props) => (props.$focused ? "-2px" : "0")});
  z-index: 2;
`;

export const PromptHalo = styled.div`
  position: absolute;
  inset: -2px;
  border-radius: 14px;
  pointer-events: none;
  opacity: ${(props) => (props.$active ? 1 : 0)};
  transform: scale(${(props) => (props.$active ? 1 : 0.97)});
  transition:
    opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  background: radial-gradient(
    120% 80% at 50% 50%,
    rgba(67, 131, 211, 0.4) 0%,
    rgba(67, 131, 211, 0.15) 45%,
    rgba(67, 131, 211, 0) 75%
  );
  filter: blur(14px);
  ${(props) =>
    props.$active &&
    css`
      animation: ${breathe} 3.2s ease-in-out infinite;
    `}
`;

export const PromptBox = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 12px;
  flex-direction: column;
  overflow: hidden;
  gap: 10px;
  border-radius: 12px;
  border: 1px solid #2877d9;
  background: linear-gradient(
    90deg,
    #13233a 0%,
    #0d1521 10.25%,
    #0a1a2f 80.12%,
    #030508 100%
  );
  transition:
    border-color 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  box-shadow: 0 0 0 0 rgba(67, 131, 211, 0);

  ${(props) =>
    props.$focused &&
    css`
      border-color: #6ba3ff;
      box-shadow:
        0 0 0 1px rgba(107, 163, 255, 0.45),
        0 8px 32px rgba(67, 131, 211, 0.18);
    `}
`;

export const PromptEdgeGlow = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 309px;
  height: 23px;
  opacity: 0.4;
  pointer-events: none;
  background: radial-gradient(
    50% 50% at 50% 50%,
    #2d78ff 0%,
    rgba(39, 102, 214, 0.81) 19.49%,
    rgba(13, 27, 45, 0) 100%
  );
  ${(props) =>
    props.$position === "bottom"
      ? css`
          bottom: -7px;
        `
      : css`
          top: -10px;
        `}
`;

export const PromptInput = styled.textarea`
  width: 100%;
  min-height: 22px;
  border: none;
  outline: none;
  resize: none;
  background: transparent;
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.21px;
  opacity: 0.8;
  transition: height 0.35s cubic-bezier(0.22, 1, 0.36, 1);

  &::placeholder {
    color: #97c5ff;
    opacity: 0.8;
  }
`;

export const PromptActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export const PromptControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AdCta = styled.a`
  position: relative;
  display: inline-flex;
  box-sizing: border-box;
  width: 135px;
  height: 24px;
  padding: 3px 6px 3px 4px;
  justify-content: center;
  align-items: center;
  gap: 3.5px;
  border-radius: 8px;
  border: 1px solid rgba(151, 197, 255, 0.24);
  background-color: #132f5d;
  background-image:
    linear-gradient(
      110deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(151, 197, 255, 0.035) 30%,
      rgba(151, 197, 255, 0.095) 50%,
      rgba(151, 197, 255, 0.035) 70%,
      rgba(255, 255, 255, 0) 100%
    ),
    linear-gradient(
      135deg,
      rgba(67, 131, 211, 0.16) 0%,
      rgba(19, 47, 93, 0.08) 52%,
      rgba(6, 13, 24, 0.2) 100%
    );
  background-size:
    340% 100%,
    100% 100%;
  color: #97c5ff;
  font-family: Figtree;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.2px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow:
    0 0 14px rgba(67, 131, 211, 0.18),
    inset 0 0 8px rgba(151, 197, 255, 0.08);
  animation: ${adCtaShimmer} 8.5s linear infinite;
  transition:
    border-color 0.25s ease,
    color 0.25s ease,
    filter 0.2s ease,
    box-shadow 0.25s ease;

  svg {
    width: 17px;
    height: 17px;
    flex: 0 0 auto;
  }

  svg path[stroke] {
    stroke: currentColor;
  }

  &:hover {
    color: #d7eaff;
    border-color: rgba(151, 197, 255, 0.5);
    filter: brightness(1.08);
    box-shadow:
      0 0 18px rgba(67, 131, 211, 0.28),
      inset 0 0 10px rgba(151, 197, 255, 0.12);
  }

  &:active {
    filter: brightness(0.96);
  }
`;

export const AdText = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
`;

export const AdBadge = styled.span`
  color: rgba(151, 197, 255, 0.72);
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.4px;
  transform: translateY(-2px);
`;

export const IconButton = styled.button`
  display: flex;
  width: 24px;
  height: 24px;
  padding: 6px 2px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: ${(props) => (props.$active ? "#4383d3" : "#132f5d")};
  border: 1px solid
    ${(props) => (props.$active ? "rgba(151, 197, 255, 0.6)" : "transparent")};
  box-shadow: ${(props) =>
    props.$active ? "0 0 12px rgba(67, 131, 211, 0.5)" : "none"};
  cursor: pointer;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    filter 0.2s ease;

  &:hover {
    filter: brightness(1.2);
  }

  &:active {
    filter: brightness(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const EnhanceButton = styled.button`
  display: flex;
  width: ${(props) => (props.$active ? "auto" : "24px")};
  height: 24px;
  padding: ${(props) => (props.$active ? "6px 8px" : "6px 2px")};
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  white-space: nowrap;
  background: ${(props) => (props.$active ? "#4383d3" : "#132f5d")};
  border: 1px solid
    ${(props) => (props.$active ? "rgba(151, 197, 255, 0.6)" : "transparent")};
  box-shadow: ${(props) =>
    props.$active ? "0 0 12px rgba(67, 131, 211, 0.5)" : "none"};
  cursor: pointer;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    padding 0.25s ease,
    filter 0.2s ease;

  &:hover {
    filter: brightness(1.2);
  }

  &:active {
    filter: brightness(0.95);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const EnhanceLabel = styled.span`
  color: #fff;
  font-family: Figtree;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.2px;
  overflow: hidden;
`;

export const GenerateButton = styled.button`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 640px;
  height: 40px;
  padding: 6px 18px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background:
    linear-gradient(
      354deg,
      rgba(0, 0, 0, 0.2) 4.41%,
      rgba(200, 224, 255, 0.2) 95.59%
    ),
    #2671cf;
  color: #fff;
  font-family: Figtree;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.07px;
  overflow: hidden;
  z-index: 1;
  transition:
    transform 0.15s ease,
    filter 0.15s ease,
    box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${(props) =>
    props.$isLoading &&
    css`
      animation: ${generatePulse} 0.85s ease-in-out infinite;

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(190, 220, 255, 0.18) 25%,
          rgba(220, 235, 255, 0.32) 50%,
          rgba(190, 220, 255, 0.18) 75%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: ${generateShimmer} 1.2s linear infinite;
        border-radius: 100px;
        pointer-events: none;
      }
    `}

  ${(props) =>
    props.$inputFocused &&
    !props.$isLoading &&
    !props.$isExploding &&
    css`
      filter: brightness(1.06);
    `}

  ${(props) =>
    props.$isExploding &&
    css`
      animation: ${generateExplode} 0.5s ease-out forwards;
    `}
`;
